import {FC, useCallback, useEffect, useState} from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Close,
    Download,
    OpenInNew,
} from '@mui/icons-material';
import {AiImageResult} from '../../types/aiImage';

interface Props {
    open: boolean;
    onClose: () => void;
    results: AiImageResult[];
    initialIndex: number;
    groupName: string;
    requestId: number;
}

const sanitizeFileName = (input: string): string => {
    return input.replace(/[^A-Za-z0-9가-힣_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

export const AiImageLightbox: FC<Props> = ({
    open,
    onClose,
    results,
    initialIndex,
    groupName,
    requestId,
}) => {
    const [index, setIndex] = useState(initialIndex);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (open) setIndex(initialIndex);
    }, [open, initialIndex]);

    const total = results.length;
    const current = total > 0 ? results[Math.min(index, total - 1)] : undefined;

    const goPrev = useCallback(() => {
        if (total === 0) return;
        setIndex((i) => (i - 1 + total) % total);
    }, [total]);

    const goNext = useCallback(() => {
        if (total === 0) return;
        setIndex((i) => (i + 1) % total);
    }, [total]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, goPrev, goNext, onClose]);

    const handleDownload = async () => {
        if (!current) return;
        setDownloading(true);
        try {
            const res = await fetch(current.imageUrl);
            const blob = await res.blob();
            const ext = (() => {
                const m = blob.type.match(/image\/(\w+)/);
                return m ? m[1] : 'png';
            })();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${sanitizeFileName(groupName)}-${requestId}-${current.sequence}.${ext}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            // fallback: 새 탭 열기
            window.open(current.imageUrl, '_blank', 'noopener');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogContent sx={{p: 0, bgcolor: 'grey.900'}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{px: 2, py: 1, bgcolor: 'grey.800', color: 'common.white'}}
                >
                    <Typography variant="body2" sx={{color: 'inherit'}}>
                        {groupName} / 요청 #{requestId} ({current ? current.sequence + 1 : 0}/{total})
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="다운로드">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleDownload}
                                    disabled={!current || downloading}
                                    sx={{color: 'common.white'}}
                                >
                                    <Download fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="새 탭에서 원본 열기">
                            <span>
                                <IconButton
                                    size="small"
                                    component="a"
                                    href={current?.imageUrl ?? '#'}
                                    target="_blank"
                                    rel="noopener"
                                    disabled={!current}
                                    sx={{color: 'common.white'}}
                                >
                                    <OpenInNew fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="닫기 (ESC)">
                            <IconButton size="small" onClick={onClose} sx={{color: 'common.white'}}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 400,
                        maxHeight: '80vh',
                        bgcolor: 'common.black',
                    }}
                >
                    {total > 1 && (
                        <IconButton
                            onClick={goPrev}
                            sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0,0,0,0.4)',
                                color: 'common.white',
                                '&:hover': {bgcolor: 'rgba(0,0,0,0.6)'},
                            }}
                        >
                            <ChevronLeft />
                        </IconButton>
                    )}
                    {current && (
                        <Box
                            component="img"
                            src={current.imageUrl}
                            alt={`result-${current.sequence}`}
                            sx={{
                                maxHeight: '80vh',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                    {total > 1 && (
                        <IconButton
                            onClick={goNext}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(0,0,0,0.4)',
                                color: 'common.white',
                                '&:hover': {bgcolor: 'rgba(0,0,0,0.6)'},
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
